import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { sortSelections, sortSelectionsByName } from 'src/domains/layouts/utils/sortSelections';
import { useCommon } from 'src/domains/common/Common';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { SelectionCast } from 'src/domains/sportsbook/webview/components/selectionCast/SelectionCast';
import { action, observable, makeObservable } from 'mobx';
import { EWInformation } from 'src/domains/sportsbook/webview/components/raceSummary/templates/rowMarkets/summaryMarkets/RaceSummaryRowMarkets.style';
import { RaceSummaryRow } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummarySport/RaceSummaryNew.style';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import {
    JockeyAndTrainerDetailsBox,
    NonRunnerCellMatchBet,
    RaceSummary,
    SelectionList,
    SilkCellRaceSummaryWrapper,
} from 'src/domains/sportsbook/webview/components/raceSummary/templates/rowMarkets/summaryMarket/RaceSummaryRowMarket.style';
import { LazyImageSilk } from 'src/domains/sportsbook/webview/components/lazyImage/LazyImageSilk';
import { CellRunner } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/horseRacing/RaceSummaryCastRowHorseRacing.style';
import { Collapse } from 'src/ui/components/collapse/sports/04/Collapse';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { UsersState } from 'src/domains/players/state/UsersState';

class MatchBetsHorseRacingClass {
    @observable public isOpen: boolean = true;

    @action public toggleIsOpen = (): void => {
        this.isOpen = !this.isOpen;
    };

    public constructor() {
        makeObservable(this);
    }
}

interface PropsType {
    marketId: MarketId;
}

export const RaceSummaryRowMarket = observer('RaceSummaryRowMarket', (props: PropsType) => {
    const { marketId } = props;
    const [state] = useState(() => new MatchBetsHorseRacingClass());
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const usersState = UsersState.get(common);

    const oddsFormat = usersState.oddsFormatShort;
    const marketModel = marketId.getModel();
    if (marketModel === null) {
        return null;
    }

    const eventModel = marketModel.eventModel;
    if (eventModel === null) {
        return null;
    }

    const marketSortFunction =
        marketModel.displayOrderTag === 'by-selection-name' ? sortSelectionsByName : sortSelections;
    const marketSelections = marketSortFunction(marketModel.selections.filter((x) => x.display));

    const breakpointsState = BreakpointsState.get(common);

    const eachWayTitle = (): JSX.Element | null => {
        const isMobile = breakpointsState.mobile.isBiggerOrEq === false;

        if (
            marketModel.eachWayOffered === false ||
            marketModel.eachWayTermsReduction === undefined ||
            marketModel.eachWayTermsPlaces === undefined
        ) {
            return null;
        }

        const ewTerms = `EW Terms: ${marketModel.eachWayTermsReduction}`;
        const places = `${marketModel.eachWayTermsPlaces} places`;
        if (isMobile === true) {
            return (
                <>
                    {ewTerms}
                    <br />
                    {places}
                </>
            );
        } else {
            return (
                <>
                    {ewTerms} {places}
                </>
            );
        }
    };

    return (
        <RaceSummary>
            <Collapse
                handleToggle={state.toggleIsOpen}
                title={marketModel.name}
                isOpen={state.isOpen}
            >
                <EWInformation>{eachWayTitle()}</EWInformation>
            </Collapse>

            <SelectionList>
                {state.isOpen === false
                    ? null
                    : marketSelections.map((selection) => {
                          const isNonRunner = selection.resultType === 'void';
                          const silkUrl = selection.metaDataSilkUrl ?? '';
                          const silkAltDesc = selection.metaDataSilk ?? 'Silk image';

                          return (
                              <RaceSummaryRow
                                  key={selection.name}
                                  isNonRunner={isNonRunner}
                                  variant={configState.componentVariants.sportsbookVariant}
                              >
                                  <CellRunner>
                                      <SilkCellRaceSummaryWrapper>
                                          <LazyImageSilk
                                              imgBase={{
                                                  src: silkUrl,
                                                  optimizeOptions: {
                                                      quality: 85,
                                                      width: 54,
                                                  },
                                              }}
                                              alt={silkAltDesc}
                                          />
                                      </SilkCellRaceSummaryWrapper>

                                      <JockeyAndTrainerDetailsBox
                                          selectionId={selection.id2}
                                          isDetailsAvailable={true}
                                      />
                                  </CellRunner>

                                  {isNonRunner ? (
                                      <NonRunnerCellMatchBet>NR</NonRunnerCellMatchBet>
                                  ) : (
                                      <SelectionCast
                                          key={selection.id}
                                          selectionId={selection.id2}
                                          title={oddsFormat === 'd' ? selection.price?.d : selection.price?.f}
                                          borderLeft={true}
                                          index={selection.id}
                                      />
                                  )}
                              </RaceSummaryRow>
                          );
                      })}
            </SelectionList>
        </RaceSummary>
    );
});
